import { ref, watch, nextTick, onMounted, onUnmounted } from "vue";

export function useCombobox(options = {}) {
  const {
    items, // Ref<Array> of items to navigate
    onSelect, // (item) => void
    onClose, // () => void
    isItemDisabled = () => false, // (item) => boolean, skipped during navigation
    initialIsOpen = false,
    keepOpenOnSelect = false,
  } = options;

  function firstEnabledIndex() {
    const index = items.value.findIndex((item) => !isItemDisabled(item));
    return index === -1 ? (items.value.length > 0 ? 0 : -1) : index;
  }

  const isOpen = ref(initialIsOpen);
  const activeIndex = ref(-1);
  const containerRef = ref(null);
  const listRef = ref(null); // The scrollable container
  const itemRefs = ref([]); // Array of item elements
  let listeningSince = 0;
  let pressedInside = false;

  function open() {
    isOpen.value = true;
    if (items.value.length > 0 && activeIndex.value === -1) {
      activeIndex.value = firstEnabledIndex();
    }
  }

  function close() {
    isOpen.value = false;
    activeIndex.value = -1;
    if (onClose) onClose();
  }

  function toggle() {
    if (isOpen.value) {
      close();
    } else {
      open();
    }
  }

  function setActiveIndex(index) {
    activeIndex.value = index;
  }

  function setItemRef(el, index) {
    if (el) {
      itemRefs.value[index] = el;
    }
  }

  async function scrollToActive() {
    await nextTick();
    if (activeIndex.value < 0 || !listRef.value) return;

    // If using itemRefs (preferred for precise control)
    const activeEl = itemRefs.value[activeIndex.value];
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" });
      return;
    }

    // Fallback: try to find by class if refs aren't used
    const listItems = listRef.value.querySelectorAll(".dropdown-item, [role='option']");
    const item = listItems[activeIndex.value];
    if (item) {
      item.scrollIntoView({ block: "nearest" });
    }
  }

  function handleKeydown(event) {
    if (!isOpen.value && ["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
      event.preventDefault();
      open();
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (items.value.length > 0) {
          activeIndex.value = (activeIndex.value + 1) % items.value.length;
          scrollToActive();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (items.value.length > 0) {
          activeIndex.value = (activeIndex.value - 1 + items.value.length) % items.value.length;
          scrollToActive();
        }
        break;
      case "Enter":
        event.preventDefault();
        if (
          isOpen.value &&
          activeIndex.value >= 0 &&
          items.value[activeIndex.value] &&
          !isItemDisabled(items.value[activeIndex.value])
        ) {
          if (onSelect) onSelect(items.value[activeIndex.value]);
          if (!keepOpenOnSelect) close();
        }
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      case "Tab":
        close();
        break;
    }
  }

  // Where the press landed, taken before any handler can re-render the page.
  function handlePointerDown(event) {
    pressedInside = !!containerRef.value?.contains(event.target);
  }

  function handleClickOutside(event) {
    const pressed = pressedInside;
    pressedInside = false;

    // A document listener added while a click is still bubbling below document
    // still receives that same click. Ignore any event that began before we started listening.
    if (event.timeStamp < listeningSince) return;

    // Opening the list swaps the caret for a spinner before the click reaches us,
    // so the clicked node is by then inside nothing -- hence the press position.
    // A keyboard-driven click has no press to go on, and falls back to the target.
    if (pressed) return;

    if (containerRef.value && !containerRef.value.contains(event.target)) {
      close();
    }
  }

  onMounted(() => {
    listeningSince = performance.now();
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("click", handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener("pointerdown", handlePointerDown, true);
    document.removeEventListener("click", handleClickOutside);
  });

  // Reset active index when items change
  watch(items, () => {
    if (isOpen.value) {
      activeIndex.value = firstEnabledIndex();
    }
  });

  return {
    isOpen,
    activeIndex,
    containerRef,
    listRef,
    itemRefs,
    open,
    close,
    toggle,
    setActiveIndex,
    setItemRef,
    handleKeydown,
    scrollToActive,
  };
}
